import debug from 'debug'
import Cookies from 'js-cookie'
import { useEffect, useRef } from 'react'
import { useMyInfo } from 'src/services/apollo/auth'
import { contractClient } from 'src/services/orpc/contract'
import { genUuid } from 'src/utils/genId'
import {
  ANONYMOUS_ID_KEY,
  CLICK_SENT_AT_KEY,
  extractAdClickParams,
  formatClickSentValue,
  parseClickSentStatus,
  parseGaClientId,
} from './helpers'

const log = debug('pixai:attribution')

// Capture the landing URL at module load time, before the router can strip query params.
const landingUrl =
  typeof window !== 'undefined' ? new URL(window.location.href) : null
if (landingUrl) log('landing URL captured: %s', landingUrl.href)

async function getOrCreateAnonymousId(): Promise<string> {
  let id = localStorage.getItem(ANONYMOUS_ID_KEY)
  if (!id) {
    id = await genUuid()
    localStorage.setItem(ANONYMOUS_ID_KEY, id)
  }
  return id
}

export function useAdClickCapture() {
  useEffect(() => {
    if (!landingUrl) return
    if (parseClickSentStatus(sessionStorage.getItem(CLICK_SENT_AT_KEY))) {
      log('click already sent this session, skipping')
      return
    }

    const adParams = extractAdClickParams(landingUrl)
    if (!adParams) {
      log('no ad click params found in URL: %s', landingUrl.search)
      return
    }

    log('ad click params detected: %o', adParams)

    // Mark as sent optimistically to prevent StrictMode double-fire duplicates.
    sessionStorage.setItem(CLICK_SENT_AT_KEY, formatClickSentValue('pending'))

    void (async () => {
      const anonymousId = await getOrCreateAnonymousId()
      const body = { anonymousId, ...adParams }

      log('sending click event: %o', body)
      try {
        await contractClient.attribution.createClickEvent(body)
        sessionStorage.setItem(
          CLICK_SENT_AT_KEY,
          formatClickSentValue('success'),
        )
      } catch (e) {
        sessionStorage.removeItem(CLICK_SENT_AT_KEY)
        log('click event failed: %o', e)
      }
    })()
  }, [])
}

export function useAttributionIdentify() {
  const { data: userInfo } = useMyInfo()
  const identifiedRef = useRef<string>(null)

  useEffect(() => {
    const userId = userInfo?.id
    if (!userId) {
      log('identify: no userId yet')
      return
    }
    if (identifiedRef.current === userId) {
      log('identify: already identified userId=%s', userId)
      return
    }
    if (
      parseClickSentStatus(sessionStorage.getItem(CLICK_SENT_AT_KEY)) !==
      'success'
    ) {
      log('identify: no click event this session, skipping')
      return
    }

    void (async () => {
      const anonymousId = await getOrCreateAnonymousId()

      const body = {
        anonymousId,
        facebookPixelId: Cookies.get('_fbp'),
        googleAnalyticsClientId: parseGaClientId(Cookies.get('_ga')),
      }
      log('sending identify for userId=%s: %o', userId, body)
      try {
        await contractClient.attribution.upsertIdentifiers(body)
        identifiedRef.current = userId
      } catch (e) {
        log('identify failed: %o', e)
      }
    })()
  }, [userInfo?.id])
}
